import m, {type ComponentType, MithrilComponent} from '@bitstillery/mithril'
import {Spinner} from '@bitstillery/common/components/portal'
import {ContentFrame} from '@/cms/components/shell/content_frame/content_frame'
import {merge_deep} from '@bitstillery/common/lib/utils'
import {watch, type Unwatch} from '@bitstillery/mithril'
import {api, cms_content_path, logger} from '@bitstillery/common/app'
import {$s} from '@/app'
import {import_compiled_cms_module, resolve_cms_component} from '@/cms/runtime/compile'
import {
    MISSING_EXPORT_ERROR,
    add_page_copy,
    ensure_page_copy,
    load_page,
    mark_page_copy_loaded,
    not_found_page,
    prime_ssr_cms_page,
    show_hero_copy,
} from '@/cms/runtime/loader'
import type {CmsPageMeta} from '@/cms/runtime/loader'
import {warm_cms_page} from '@/cms/runtime/warmup'
import {CmsNotFound} from '@/cms/components/not_found/not_found'
import {PAGE_NOT_FOUND, mark_ssr_not_found} from '@/cms/lib/not_found'

// Re-exported so existing importers (e.g. the preview frame) keep their `@/cms/runtime/page` path;
// the implementations live in `compile.ts` so `fragments.ts` can reuse them without an import cycle.
export {import_compiled_cms_module, resolve_cms_component}

/**
 * Pre-compile the SSR-hydrated CMS page before the first client render.
 *
 * Compiling a CMS module is async (`import(blobURL)`), but `CmsDynamicPage.oninit` runs during the
 * first hydration render — its `await` yields before `this.component` is set, so `view()` paints the
 * loading Spinner over the correct server HTML and the page flashes (content → spinner → content).
 * Seeding the cache here, before `m.route`, lets oninit take the synchronous cache-hit branch and
 * hydrate the real component on the first frame — no flash.
 *
 * Keyed by `current_cms_path()` (what oninit looks up); `$s.env.uri` is already populated by the
 * time app.init reaches this point. No-op on the server and when there is no hydrated page. On a
 * compile error the cache stays cold and oninit retries, surfacing it via `$s.page.error`.
 */
export async function prewarm_cms_page_from_ssr(): Promise<void> {
    if (globalThis.__SSR_MODE__ || !$s.page.compiled_js) {
        return
    }
    try {
        const page_path = current_cms_path()
        // `init_language` (run just before this) already loaded this page's copy into the bundle, so
        // record it as live — otherwise the cache-hit hydration below would refetch and lose its
        // synchronous fast path, flashing a Spinner over the server HTML.
        mark_page_copy_loaded($s.language, page_path)
        await prime_ssr_cms_page(page_path)
    } catch {
        // Ignore; CmsDynamicPage.oninit will compile again and report the error.
    }
}

export function is_cms_route(): boolean {
    return !$s.env.uri.startsWith('/shop') && !$s.env.uri.startsWith('/my-portal')
}

/**
 * SSR: fetch the CMS page for the current URL into `$s.page` before the first render. The chrome
 * (ShellHeader's hero + breadcrumbs) sits above `CmsDynamicPage` in the component tree, so it is
 * stringified before this page's `oninit` has loaded anything — without the prefetch the server
 * HTML gets the default chrome (full hero, no photo, no auth-collapsed band) and the real state
 * flashes in on hydration. Called from `AppPortal.init` (server only); `load_cms_page` detects the
 * prefetched state and compiles it instead of fetching again. Self-guards: on failure the state
 * stays cold and `load_cms_page` fetches as before.
 */
export async function prefetch_cms_page_for_ssr(): Promise<void> {
    if (!globalThis.__SSR_MODE__ || !is_cms_route()) {
        return
    }
    try {
        const page_path = current_cms_path()
        const {result: data, status_code, success} = await api.get('portal/cms/page', {path: page_path})
        if (status_code === 404) {
            mark_ssr_not_found()
            merge_deep($s.page, not_found_page(page_path))
            return
        }
        if (!success) {
            return
        }
        merge_deep($s.page, {
            compiled_js: data.compiled_js,
            error: null,
            hero_background: data.hero_background,
            hero_mode: data.hero_mode,
            path: data.path,
        })
    } catch {
        // Ignore; CmsDynamicPage falls back to its own fetch and reports errors itself.
    }
}

/**
 * Which content wrapper `CmsDynamicPage` renders. Keyed to the *committed* route (`m.route.get()`),
 * the same source `LayoutPortal` uses for its `cms-layout`/`shop-layout` class — NOT `$s.env.uri`.
 * The two desync during a cms→shop navigation: `locationchange` sets `$s.env.uri` to `/shop`
 * immediately, but `m.route.get()` stays on the old path until the route resolves. Reading
 * `$s.env.uri` here would drop the `ContentFrame` wrapper for the still-mounted CMS page while the
 * layout is still `cms-layout`, so its content loses its width cap and flashes blown-up for a frame.
 */
export function is_shop_layout(): boolean {
    return m.route.get().startsWith('/shop')
}

/** The CMS page to render for the current URL: the home page behind overlay routes, else the URL. */
function current_cms_path(): string {
    return cms_content_path($s.env.uri)
}

export class CmsDynamicPage extends MithrilComponent {
    component: ComponentType | undefined
    /**
     * The CMS path currently rendered by `this.component`. Drives the content `key`; the chrome
     * (hero + breadcrumbs in `ShellHeader`) reads `$s.page.path`, which `commit_page` sets in the
     * same atomic swap — so both hold on the current page until the replacement is ready instead
     * of jumping ahead of the loading content.
     */
    rendered_path = ''
    /**
     * Monotonic id claimed by each `load_cms_page` call. Rapid navigation fires overlapping loads that
     * share `$s.page`/`this.component`; a load compares this against its own claimed id after every
     * await and bails if a newer load has since started, so a slow load can't commit its stale page (or
     * clobber the newer one) after a later navigation already rendered.
     */
    load_generation = 0
    watchers: Unwatch[] = []

    override async oninit() {
        if (is_cms_route()) {
            await this.load_cms_page()
        } else {
            $s.page.hero_background = ''
        }

        if (!globalThis.__SSR_MODE__) {
            // Deal with back/forward navigation.
            this.watchers.push(
                watch($s.env.$uri, () => {
                    if (is_cms_route()) {
                        void this.load_cms_page()
                    } else {
                        $s.page.hero_background = ''
                    }
                }),
            )
        }
    }

    override onremove() {
        for (const unwatch of this.watchers) {
            unwatch()
        }
    }

    /** Compile the CMS module currently in `$s.page.compiled_js` into `this.component` and cache it. */
    async compile_into_component(page_path: string): Promise<void> {
        try {
            const cms_component = await prime_ssr_cms_page(page_path)
            if (!cms_component) {
                $s.page.error = MISSING_EXPORT_ERROR
                return
            }
            this.component = cms_component
            this.rendered_path = page_path
        } catch (e) {
            $s.page.error = e instanceof Error ? e.message : String(e)
        }
    }

    /**
     * Swap the rendered page in one synchronous step: page meta + copy + component + path together.
     * Keeping this atomic (and only calling it once the next component is compiled) is what lets the
     * current page stay fully rendered until the replacement is ready — no empty/loading in between.
     */
    commit_page(page_path: string, component: ComponentType, page: CmsPageMeta): void {
        merge_deep($s.page, page)
        show_hero_copy(page_path)
        this.component = component
        this.rendered_path = page_path
    }

    async load_cms_page(): Promise<void> {
        // Overlay routes (/login, /register, …) render the '/' home page behind their dialog, so the
        // CMS page + i18n always come from `current_cms_path()`, not the literal address-bar URL.
        const page_path = current_cms_path()
        // Claim a generation so overlapping loads from rapid navigation can detect being superseded and
        // bail before mutating shared render state, rather than committing a stale page or i18n.
        const generation = ++this.load_generation
        const superseded = () => this.load_generation !== generation

        /**
         * Show `component`, but not before it has loaded its content — the page already on screen keeps
         * rendering meanwhile (see `runtime/warmup`). With nothing rendered yet there is nothing to keep
         * showing and nothing to wait for: that is the SSR hydration, which takes the cache-hit path
         * below, and an await here would let `view()` paint a Spinner over the server's HTML.
         */
        const commit_when_warm = async (component: ComponentType, page: CmsPageMeta) => {
            if (this.component) {
                await warm_cms_page(page_path, component)
                if (superseded()) return
            }
            this.commit_page(page_path, component, page)
        }

        if (!globalThis.__SSR_MODE__) {
            // Browser clients cache the compiled cms page across visits. The copy is NOT cached with it:
            // the bundle is language-scoped, so ensure the current language's page keys are present
            // before rendering — a cache hit in a freshly switched language would otherwise show raw keys.
            const cached_page = load_page.peek(page_path)
            if (cached_page?.component) {
                logger.info(`cms page cache hit for ${page_path}`)
                // Fast path: when this language's copy is already in the bundle — or merely already
                // fetched, as a link prefetch leaves it — commit synchronously, so a prewarmed hydration
                // or a prefetched navigation renders the real component on the first frame (no Spinner
                // flash). Only await a fetch when the copy is genuinely missing (e.g. after a language
                // switch), where the current page stays rendered until the atomic swap anyway.
                if (!add_page_copy($s.language, page_path)) {
                    await ensure_page_copy(page_path)
                    if (superseded()) return
                }
                await commit_when_warm(cached_page.component, cached_page.page)
                return
            }

            // First render after SSR: this page (compiled_js + meta) is already hydrated in $s.page
            // (serialized via portalStore) and `init_language` already loaded its copy. Compile it
            // directly instead of re-fetching — otherwise hydration flashes a spinner over the
            // server-rendered HTML — and record that copy as live so revisits don't refetch.
            if (!this.component && $s.page.compiled_js && $s.page.path === page_path) {
                logger.info(`hydrating cms page from ssr state for ${page_path}`)
                mark_page_copy_loaded($s.language, page_path)
                await this.compile_into_component(page_path)
                return
            }
        }

        // SSR: `prefetch_cms_page_for_ssr` (app.init) already fetched this page into `$s.page` so
        // the chrome could render it ahead of this component — compile from state, don't refetch.
        if (globalThis.__SSR_MODE__ && $s.page.path === page_path) {
            if ($s.page.compiled_js) {
                await this.compile_into_component(page_path)
                return
            }
            if ($s.page.error) {
                this.component = undefined
                this.rendered_path = page_path
                return
            }
        }

        // A cold page and its copy are independent fetches, and nothing of this page is on screen while
        // they run — so request them together. Chained (copy first, as it used to be) they cost two round
        // trips of empty `<main>` on a navigation that starts from a non-CMS route. `load_page` compiles
        // the replacement before any shared state is touched, so the page being navigated away from stays
        // fully rendered until the atomic `commit_page` below.
        const [loaded] = await Promise.all([
            load_page(page_path),
            globalThis.__SSR_MODE__ ? Promise.resolve() : ensure_page_copy(page_path),
        ])
        if (superseded()) return

        if (loaded.page.error === PAGE_NOT_FOUND) {
            mark_ssr_not_found()
        }
        if (!loaded.component) {
            this.component = undefined
            this.rendered_path = page_path
            merge_deep($s.page, loaded.page)
            return
        }

        await commit_when_warm(loaded.component, loaded.page)
    }

    view() {
        // The content `key` changes only when `commit_page` swaps the page in, so Mithril recreates
        // the <main> and replays the fade-in — turning the atomic swap into a quick cross-fade.
        const inner = () => {
            // A path that resolves to no published page is a visitor-facing 404 (its copy lives in the
            // hero, from the global `page.404.*` placeholders); anything else in `error` is a compile or
            // render failure, which stays a developer-facing message.
            if ($s.page.error === PAGE_NOT_FOUND) {
                return (
                    <main key={`not-found:${this.rendered_path}`} class='cms-dynamic-page cms-dynamic-page--404 page-content'>
                        <CmsNotFound />
                    </main>
                )
            }

            if ($s.page.error) {
                return (
                    <main key={`error:${this.rendered_path}`} class='cms-dynamic-page cms-dynamic-page--error page-content'>
                        {$s.page.error}
                    </main>
                )
            }

            if (!this.component) {
                return (
                    <main key='loading' class='cms-dynamic-page cms-dynamic-page--loading page-content'>
                        <Spinner />
                    </main>
                )
            }

            return (
                <main key={this.rendered_path} class='cms-dynamic-page page-content'>
                    {m(this.component)}
                </main>
            )
        }

        if (is_shop_layout()) {
            return <div class='cms-dynamic-page-root cms-dynamic-page-root--portal'>{inner()}</div>
        }

        return (
            <ContentFrame>
                <div class='content'>{inner()}</div>
            </ContentFrame>
        )
    }
}
